import React, { FC, Fragment } from 'react';
import { Box, Chip, TableCell, TableRow, Theme, Typography, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ActiveIndicator from 'components/users/shared/active-indicator/active-indicator.component';
import { CustomerType, LabClient } from 'models/client-directory';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import { transportProviderName } from 'utils/transport-provider-name';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';

type ClientDirectoryProps = {
    client: LabClient;
    isPtjOrg: boolean;
};
const ClientDirectoryItem: FC<ClientDirectoryProps> = ({ client, isPtjOrg }: ClientDirectoryProps) => {
    const { t: translate } = useTranslation(['client-directory', 'transport-providers']);
    const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const handleClientClick = (clientId: string) => {
        navigate(`/client-directory/clients/${clientId}/client-details#contactDetails`);
    };

    const chipColor = (type: CustomerType | undefined) => {
        if (type) {
            if (type == CustomerType.DENTHUB_CONNECTED) {
                return 'focus.dark';
            } else if (type == CustomerType.SUPPLIER) {
                return 'extra.dark';
            }
        }
        return 'calm.main';
    };

    const getClientType = (client: LabClient) => {
        if (client.customer.isWhiteLabeled) {
            return isSmallScreen
                ? translate(`client-directory:MOBILE_WHITE_LABEL`)
                : translate(`client-directory:WHITE_LABEL`);
        }
        switch (client?.customer?.customerType) {
            case CustomerType.SUPPLIER:
                return isSmallScreen
                    ? translate(`client-directory:MOBILE_LAB_CUSTOMER`)
                    : translate(`client-directory:LAB_CUSTOMER`);
            case CustomerType.MY_CLIENT:
                return isSmallScreen
                    ? translate(`client-directory:MOBILE_MY_CLIENT`)
                    : translate(`client-directory:MY_CLIENT`);
            case CustomerType.DENTHUB_CONNECTED:
                return isSmallScreen
                    ? translate(`client-directory:MOBILE_DENTHUB_CONNECTED`)
                    : translate(`client-directory:DENTHUB_CONNECTED`);
            default:
                return isSmallScreen
                    ? translate(`client-directory:MOBILE_MY_CLIENT`)
                    : translate(`client-directory:MY_CLIENT`);
        }
    };

    return (
        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => handleClientClick(client.customer._id)}>
            <Fragment>
                <TableCell>
                    {client.customer.customerType === CustomerType.DENTHUB_CONNECTED &&
                    client.customer.parentClinic?.name ? (
                        <Box ml={0.25} display="flex" alignItems="center">
                            <SubdirectoryArrowRightIcon sx={{ color: 'grey.300' }} />
                            <Box ml={0.5}>
                                <Box>{client.customer.name}</Box>
                                <Typography variant="caption" color="grey.500">
                                    {`${translate(`client-directory:BELONGS_TO`)}: ${client.customer.parentClinic?.name}`}
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Box>
                            {client.customer.name}
                            {client.customer.isMain ? (
                                <Fragment>
                                    <Chip
                                        size="small"
                                        sx={{ marginLeft: 1 }}
                                        label={translate(`client-directory:MAIN`)}
                                    />
                                </Fragment>
                            ) : undefined}
                        </Box>
                    )}
                </TableCell>
                {isPtjOrg && (
                    <TableCell>{client.ptjCustomerNumber || client.customer.ptjCustomerNumber || '-'}</TableCell>
                )}
                <TableCell>
                    {client.customer.actionListName && (
                        <Fragment>
                            {client.customer.actionListName === 'procurement_price'
                                ? translate('client-directory:PROCUREMENT_PRICE')
                                : client.customer.actionListName}
                        </Fragment>
                    )}
                </TableCell>
                <TableCell>{client.customer.materialListName}</TableCell>
                {!isSmallScreen ? (
                    <TableCell>
                        <Chip
                            size="small"
                            sx={{
                                cursor: 'pointer',
                                color: chipColor(client?.customer.customerType),
                                borderColor: chipColor(client?.customer.customerType),
                            }}
                            label={getClientType(client)}
                        />
                    </TableCell>
                ) : (
                    <TableCell>
                        <Box
                            sx={{
                                height: '1.8rem',
                                width: '1.8rem',
                                borderRadius: '100%',
                                backgroundColor:
                                    client.customer.customerType === CustomerType.MY_CLIENT ? '#0288D1' : '#F7BC9F',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    backgroundColor:
                                        client.customer.customerType === CustomerType.MY_CLIENT ? '#0288D1' : '#F7BC9F',
                                    color: '#fff',
                                    borderRadius: '100%',
                                    fontSize: '.8rem',
                                }}
                            >
                                {getClientType(client)}
                            </Typography>
                        </Box>
                    </TableCell>
                )}

                <TableCell align="right" sx={{ display: 'flex' }}>
                    {client.invoiceEnabled && (
                        <Chip
                            size="small"
                            sx={{ cursor: 'pointer', marginRight: 1 }}
                            icon={<AccountBalanceWalletOutlinedIcon />}
                            label={translate('client-directory:INVOICING')}
                        />
                    )}
                    {client.customer.isTransportEnabled && (
                        <Chip
                            size="small"
                            sx={{ cursor: 'pointer', marginRight: 1 }}
                            icon={<LocalShippingOutlinedIcon />}
                            label={translate(transportProviderName(client.transportProvider))}
                        />
                    )}
                </TableCell>
                {!isMobile && (
                    <TableCell sx={{ width: 10 }} align="center">
                        <ActiveIndicator isActive={client?.isActive || false} />
                    </TableCell>
                )}
            </Fragment>
        </TableRow>
    );
};

export default ClientDirectoryItem;
